// react
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import PersonIcon from '@mui/icons-material/Person';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

// material ui
import {
  alpha,
  ButtonBase,
  Card,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import { useGetAccountsQuery } from '@/api/recalc/endpoints/accountsApiSlice';

// components
import { useLocalStorage } from '@/hooks/useLocalStorage';
import {
  getSelectedAccount,
  setSelectedAccount,
} from '@/redux/selectedAccountSlice';

// Admin users can have access to thousands of accounts. Rendering every
// matching account as a MenuItem (each with its own Tooltip/IconButton) is
// what makes the popover slow to open, so we cap how many rows are mounted
// at once and ask the user to refine their search for the rest.
const MAX_VISIBLE_ACCOUNTS = 100;

const AccountSelect = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation([
    'glossary',
    'account-management',
    'tool-bar',
    'common',
  ]);

  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const itemRefs = React.useRef([]);

  // Account routing (URL + cookie + accounts)
  const { accountId } = useParams({ strict: false });
  const [cookieAccountId, setCookieAccountId] = useLocalStorage(
    'recalc_account_id',
    0,
  );
  // Favorite accounts, persisted as an array of AccountId strings.
  const [favoriteAccountIds, setFavoriteAccountIds] = useLocalStorage(
    'recalc_favorite_account_ids',
    [],
  );
  // Accounts from API
  const {
    data: accounts,
    isLoading: isLoadingAccounts,
    isSuccess: accountsIsSuccess,
  } = useGetAccountsQuery();

  // Redux selected account
  const selectedAccount = useSelector(getSelectedAccount);

  const navigateToSamePageWithNewAccount = React.useCallback(
    (newAccountId) => {
      // Current canonical path: /:accountId/...
      // We want to replace the first segment (old account) with newAccountId
      const currentPath = location.pathname;

      let pathWithoutAccount = currentPath;
      if (accountId) {
        const re = new RegExp(`^/${accountId}`);
        pathWithoutAccount = currentPath.replace(re, '') || '/';
      }

      const nextPath = `/${newAccountId}${
        pathWithoutAccount === '/' ? '' : pathWithoutAccount
      }`;

      navigate({
        to: `${nextPath}`,
        replace: true,
        state: location.state,
      });
    },
    [accountId, location.pathname, location.state, navigate],
  );

  // Initialize current/selected account when accounts + accountId are ready.
  // Note: initial redirect to an account (when landing on "/" without an
  // accountId in the URL) is handled by the home route, not here - this
  // effect only keeps redux/the cookie in sync with the resolved account.
  useEffect(() => {
    if (!accountsIsSuccess || !accounts || !accounts.length) return;

    // No accountId in URL - try cookie
    const accountIdToUse = accountId || cookieAccountId;

    const initialAccount =
      accounts.find(
        (account) => account.AccountId.toString() === accountIdToUse.toString(),
      ) || accounts[0];

    // Check if the account has actually changed
    const accountChanged =
      selectedAccount?.AccountId !== initialAccount.AccountId;

    if (accountChanged) {
      dispatch(setSelectedAccount(initialAccount));
      setCookieAccountId(initialAccount.AccountId);
    }
  }, [
    accountsIsSuccess,
    accounts,
    accountId,
    dispatch,
    setCookieAccountId,
    cookieAccountId,
    selectedAccount,
  ]);

  const filterOptions = (options, query) => {
    if (!query) return options;
    const lowerInput = query.toLowerCase();
    const startsWith = [];
    const includes = [];

    options?.forEach((option) => {
      const name = option.Name?.toLowerCase();
      const id = option.AccountId.toString();
      if (name?.startsWith(lowerInput) || id?.startsWith(query)) {
        startsWith.push(option);
      } else if (name?.includes(lowerInput) || id?.includes(query)) {
        includes.push(option);
      }
    });

    return [...startsWith, ...includes];
  };

  const isFavoriteAccount = (accountId) =>
    favoriteAccountIds.includes(accountId.toString());

  const handleToggleFavorite = (event, accountId) => {
    event.stopPropagation();
    const idString = accountId.toString();
    setFavoriteAccountIds((previousIds) =>
      previousIds.includes(idString)
        ? previousIds.filter((id) => id !== idString)
        : [...previousIds, idString],
    );
  };

  const searchFilteredAccounts = filterOptions(accounts, searchQuery) || [];
  // Favorites are sorted to the top, preserving relative order otherwise.
  const sortedAccounts = [...searchFilteredAccounts].sort((a, b) => {
    const aIsFavorite = isFavoriteAccount(a.AccountId);
    const bIsFavorite = isFavoriteAccount(b.AccountId);
    if (aIsFavorite === bIsFavorite) return 0;
    return aIsFavorite ? -1 : 1;
  });
  const totalMatchCount = sortedAccounts.length;
  // Only mount a bounded number of rows - with thousands of accounts,
  // rendering every match (each with its own Tooltip/IconButton) is what
  // makes the popover slow to open and type into.
  const filteredAccounts = sortedAccounts.slice(0, MAX_VISIBLE_ACCOUNTS);
  const hiddenMatchCount = totalMatchCount - filteredAccounts.length;

  // Auto-scroll the keyboard-highlighted option into view (DOM manipulation
  // that can't be done declaratively).
  useEffect(() => {
    if (!open) return;
    itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [open, highlightedIndex]);

  const handleClose = (event) => {
    if (anchorEl?.contains(event?.target)) return;
    setOpen(false);
    setSearchQuery('');
    setHighlightedIndex(0);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    setSearchQuery('');
    setHighlightedIndex(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setHighlightedIndex(0);
  };

  const handleSearchKeyDown = (event) => {
    if (!filteredAccounts.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex + 1 >= filteredAccounts.length ? 0 : prevIndex + 1,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex - 1 < 0 ? filteredAccounts.length - 1 : prevIndex - 1,
        );
        break;
      case 'Enter':
        event.preventDefault();
        handleAccountSelect(filteredAccounts[highlightedIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
  };

  const handleAccountSelect = (value) => {
    if (!value) return;

    dispatch(setSelectedAccount(value));
    setOpen(false);
    setSearchQuery('');

    if (location.pathname.includes('energyxplorer')) {
      // Clear energyGroupId from URL when switching accounts
      const basePath = `/${value.AccountId}/energyxplorer`;
      navigate({ to: `${basePath}` });
      return;
    }
    if (location.pathname.includes('dashboards')) {
      // Clear energyGroupId from URL when switching accounts
      const basePath = `/${value.AccountId}/dashboards`;
      navigate({ to: `${basePath}` });
      return;
    }
    if (location.pathname.includes('powerbi')) {
      // Clear energyGroupId from URL when switching accounts
      const basePath = `/${value.AccountId}/powerbi`;
      navigate({ to: `${basePath}` });
      return;
    }
    if (location.pathname.includes('gateways')) {
      // Clear energyGroupId from URL when switching accounts
      const basePath = `/${value.AccountId}/gateways`;
      navigate({ to: `${basePath}` });
      return;
    }
    if (location.pathname.includes('devices')) {
      // Keep users on the external sensors feature when switching account
      const basePath = `/${value.AccountId}/devices`;
      navigate({ to: `${basePath}` });
      return;
    }
    if (location.pathname.includes('sensors')) {
      // Clear energyGroupId from URL when switching accounts
      const basePath = `/${value.AccountId}/sensors`;
      navigate({ to: `${basePath}` });
      return;
    }
    if (location.pathname.includes('units')) {
      // Clear energyGroupId from URL when switching accounts
      const basePath = `/${value.AccountId}/units`;
      navigate({ to: `${basePath}` });
      return;
    }

    navigateToSamePageWithNewAccount(value.AccountId);
  };

  const targetAccountId = selectedAccount?.AccountId || accountId;

  const handleRefreshAccounts = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = `/auth/silent-login?returnTo=${encodeURIComponent(location.pathname)}`;
  };

  const handleNavigateToCreateAccount = () => {
    setOpen(false);
    navigate({ to: `/account/create` });
  };

  const handleNavigateToSettings = () => {
    setOpen(false);
    navigate({ to: `/${targetAccountId}/account/profile` });
  };

  return (
    <>
      <Tooltip title={t('glossary:account')}>
        <ButtonBase
          ref={setAnchorEl}
          onClick={handleToggle}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          data-testid="account-select"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            borderRadius: 2,
            px: 1,
            py: 0.5,
            backgroundColor: alpha('#919191', 0.1),
            ':hover': {
              backgroundColor: alpha('#919191', 0.2),
            },
          }}
        >
          <PersonIcon sx={{ color: '#919191' }} />
          {isLoadingAccounts ? (
            <CircularProgress size={16} sx={{ color: '#919191' }} />
          ) : (
            <Typography
              variant="body1"
              fontWeight={600}
              noWrap
              sx={{ minWidth: 0 }}
            >
              {selectedAccount?.Name || t('account:messages.noAccount')}
            </Typography>
          )}
          <UnfoldMoreRoundedIcon fontSize="small" sx={{ color: '#919191' }} />
        </ButtonBase>
      </Tooltip>

      <Popper
        open={open}
        anchorEl={anchorEl}
        transition
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 4 } },
        ]}
        sx={{ zIndex: 1301 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Card variant="outlined" sx={{ overflow: 'hidden' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  <TextField
                    autoFocus
                    fullWidth
                    placeholder={t('common:search', {
                      defaultValue: 'Filter accounts...',
                    })}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    sx={{
                      p: 1.5,
                    }}
                  />
                  <Divider />
                  <MenuList
                    sx={{
                      p: 0.5,
                      maxHeight: 300,
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'transparent transparent',
                      '&:hover': {
                        scrollbarColor: `${theme.palette.divider} transparent`,
                      },
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'transparent',
                        borderRadius: '3px',
                        transition: 'background-color 0.2s ease',
                      },
                      '&:hover::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.divider,
                      },
                    }}
                    dense
                  >
                    {isLoadingAccounts && (
                      <MenuItem disabled sx={{ borderRadius: '5px' }}>
                        <ListItemText
                          primary={t('common:loading', {
                            defaultValue: 'Loading...',
                          })}
                        />
                      </MenuItem>
                    )}
                    {!isLoadingAccounts && filteredAccounts.length === 0 && (
                      <MenuItem disabled sx={{ borderRadius: '5px' }}>
                        <ListItemText
                          primary={t('common:noResults', {
                            defaultValue: 'No accounts found',
                          })}
                        />
                      </MenuItem>
                    )}
                    {!isLoadingAccounts &&
                      filteredAccounts.map((option, index) => {
                        const isFavorite = isFavoriteAccount(option.AccountId);
                        return (
                          <MenuItem
                            key={option.AccountId}
                            ref={(el) => {
                              itemRefs.current[index] = el;
                            }}
                            selected={
                              option.AccountId === selectedAccount?.AccountId
                            }
                            onClick={() => handleAccountSelect(option)}
                            sx={{
                              borderRadius: '5px',
                              '&:hover .favorite-toggle-button': {
                                opacity: 1,
                              },
                              ...(index === highlightedIndex && {
                                backgroundColor: theme.palette.action.hover,
                              }),
                            }}
                            data-testid={`account-option-${option.Name}`}
                          >
                            <ListItemText primary={option.Name} />
                            <Tooltip
                              title={
                                isFavorite
                                  ? t('account-management:favorites.remove', {
                                      defaultValue: 'Remove from favorites',
                                    })
                                  : t('account-management:favorites.add', {
                                      defaultValue: 'Add to favorites',
                                    })
                              }
                            >
                              <IconButton
                                size="small"
                                className="favorite-toggle-button"
                                onClick={(e) =>
                                  handleToggleFavorite(e, option.AccountId)
                                }
                                data-testid={`account-option-favorite-${option.Name}`}
                                sx={{
                                  ml: 1,
                                  opacity: isFavorite ? 1 : 0,
                                  transition: 'opacity 0.15s ease',
                                }}
                              >
                                {isFavorite ? (
                                  <StarRoundedIcon
                                    fontSize="small"
                                    sx={{ color: theme.palette.warning.light }}
                                  />
                                ) : (
                                  <StarBorderRoundedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </MenuItem>
                        );
                      })}
                    {!isLoadingAccounts && hiddenMatchCount > 0 && (
                      <MenuItem disabled sx={{ borderRadius: '5px' }}>
                        <ListItemText
                          primary={t('common:moreResults', {
                            count: hiddenMatchCount,
                            defaultValue: `+${hiddenMatchCount} more - refine your search`,
                          })}
                        />
                      </MenuItem>
                    )}
                  </MenuList>
                  <Divider />
                  <MenuList sx={{ p: 0.5 }} dense>
                    <MenuItem
                      sx={{ borderRadius: '5px' }}
                      onClick={handleNavigateToCreateAccount}
                      data-testid="account-select-create-account"
                    >
                      <ListItemIcon>
                        <AddRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('tool-bar:toolbar.createAccount')}
                      />
                    </MenuItem>
                    <MenuItem
                      sx={{ borderRadius: '5px' }}
                      onClick={handleNavigateToSettings}
                      data-testid="account-select-settings"
                      disabled={isLoadingAccounts || !targetAccountId}
                    >
                      <ListItemIcon>
                        <ManageAccountsRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('tool-bar:toolbar.manageAccount')}
                      />
                    </MenuItem>

                    <MenuItem
                      sx={{ borderRadius: '5px' }}
                      onClick={handleRefreshAccounts}
                      data-testid="account-select-refresh-accounts"
                    >
                      <ListItemIcon>
                        <RefreshRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('account-management:refresh-accounts')}
                      />
                    </MenuItem>
                  </MenuList>
                </div>
              </ClickAwayListener>
            </Card>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default AccountSelect;
